/**
 * @fileoverview Hook personalizado para mostrar notificaciones toast usando MUI
 * @module UseToast
 */

import { useState } from "react";
import { Snackbar, Alert } from "@mui/material";

/**
 * Niveles de severidad disponibles para los toast
 * @typedef {'success' | 'error' | 'info' | 'warning'} Severity
 */

/**
 * Hook personalizado para gestionar notificaciones toast
 *
 * @example
 * import { useToast } from './useToast';
 *
 * const { Toast, showToast } = useToast();
 *
 * return (
 *   <>
 *     <Button onClick={() => showToast("Operación exitosa", "success")}>
 *       Mostrar Toast
 *     </Button>
 *     {Toast}
 *   </>
 * );
 */
export function useToast() {
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /**
   * Muestra una notificación toast con el mensaje y severidad especificados
   *
   * @param {string} message - El mensaje a mostrar en el toast
   * @param {Severity} [severity='success'] - El nivel de severidad del toast:
   * - `success` (Verde) - Para operaciones exitosas
   * - `error` (Rojo) - Para errores o fallos
   * - `warning` (Amarillo) - Para advertencias
   * - `info` (Azul) - Para información general
   *
   * @example
   * showToast("Guardado correctamente", "success");
   * showToast("Error al guardar", "error");
   */
  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const Toast = (
    <Snackbar
      open={toast.open}
      autoHideDuration={3000}
      onClose={() => setToast({ ...toast, open: false })}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {toast.message}
      </Alert>
    </Snackbar>
  );

  return { showToast, Toast };
}
